import MediaLibraryClient from '../clients/MediaLibraryClient';
import { displayErrorFeedbackMessage, stringToHTML } from '../utilities';
import Mustache from 'mustache';

export default class MediaLibraryAsset
{
    constructor()
    {
        // Imports
        this.mediaLibraryClient = new MediaLibraryClient();

        // Feedback
        this.downloadFailedFeedback = document
            .querySelector('meta[name="feedback.media.library.asset.download.failed"]')
            .getAttribute('content');

        // Helpers
        this.thumbnailPath = 'https://downloads.orion-wholesale.com/thumbnails/';
        this.downloadPath = 'https://downloads.orion-wholesale.com/prepared/';
    }

    /**
     * @param {object} json Asset data as json
     * @param {string} template Template to be rendered
     * @param {HTMLElement} container Container element
     */
    initAssets(json, template, container)
    {
        let assets = this.processAssetData(json);

        assets.forEach(asset =>
        {
            let element = stringToHTML(Mustache.render(template, asset));

            this.initEvents(element);

            this.renderAsset(container, element, asset);
        });
    }

    /**
     * @param {Object} json all assets
	 */
    processAssetData(json)
    {
        let assetData = [];

        json.items.forEach(asset =>
        {
            let dateLocaleCode = orion.getLocaleCode().replace('_', '-');
            let dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

            let data = {
                assetId: asset.attributes.assetId,
                productNumber: asset.attributes.productNumbers ? JSON.parse(asset.attributes.productNumbers)[0] : null,
                eanCode: asset.attributes.eanCode ? JSON.parse(asset.attributes.eanCode) : null,
                isNew: asset.isNew,
                isVideoOrThreeSixty: asset.assetFiles['none'] && (asset.assetFiles['none'].filename.includes('.mp4')
                    || asset.assetFiles['none'].filename.includes('.zip')),
                isVideo: !!(asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.mp4')),
                isThreeSixty: !!(asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.zip')),
                isProductImage: !!(asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.psd')),
                isLogoImage: !!(asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.svg')),
                thumbnail: asset.assetFiles['thumbnail-200']
                    ? this.thumbnailPath + asset.assetFiles['thumbnail-200'].filename
                    : `/assets/images/missing/product-list-image-${orion.getLocaleCode()}.jpg`,
                videoFilePath: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.mp4')
                    ? `${asset.assetFiles['none'].path}/${asset.assetFiles['none'].filename}` : null,
                videoFileSize: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.mp4')
                    ? (asset.assetFiles['none'].size / (1024 ** 2)).toFixed(2) : null,
                archiveFilePath: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.zip')
                    ? `${asset.assetFiles['none'].path}/${asset.assetFiles['none'].filename}` : null,
                archiveFileSize: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.zip')
                    ? (asset.assetFiles['none'].size / (1024 ** 2)).toFixed(2) : null,
                psdFilePath: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.psd')
                    ? `${asset.assetFiles['none'].path}/${asset.assetFiles['none'].filename}` : null,
                psdFileSize: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.psd')
                    ? (asset.assetFiles['none'].size / (1024 ** 2)).toFixed(2) : null,
                jpgFilePath: asset.assetFiles['jpeg-max'] && asset.assetFiles['jpeg-max'].filename.includes('.jpeg')
                    ? `${asset.assetFiles['jpeg-max'].path}/${asset.assetFiles['jpeg-max'].filename}` : null,
                jpgFileSize: asset.assetFiles['jpeg-max']
                    ? (asset.assetFiles['jpeg-max'].size / (1024 ** 2)).toFixed(2) : null,
                pngFilePath: asset.assetFiles['png-max'] && asset.assetFiles['png-max'].filename.includes('.png')
                    ? `${asset.assetFiles['png-max'].path}/${asset.assetFiles['png-max'].filename}` : null,
                pngFileSize: asset.assetFiles['png-max']
                    ? (asset.assetFiles['png-max'].size / (1024 ** 2)).toFixed(2) : null,
                svgFilePath: asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.svg')
                    ? `${asset.assetFiles['none'].path}/${asset.assetFiles['none'].filename}` : null,
                svgFileSize: asset.assetFiles['none']
                    ? (asset.assetFiles['none'].size / (1024 ** 2)).toFixed(2) : null,
                epsFilePath: asset.assetFiles['vector-eps'] && asset.assetFiles['vector-eps'].filename.includes('.eps')
                    ? `${asset.assetFiles['vector-eps'].path}/${asset.assetFiles['vector-eps'].filename}` : null,
                epsFileSize: asset.assetFiles['vector-eps']
                    ? (asset.assetFiles['vector-eps'].size / (1024 ** 2)).toFixed(2) : null,
                label: asset.attributes.label,
                title: asset.attributes.title,
                width: asset.attributes.widthInPixel,
                height: asset.attributes.heightInPixel,
                addedTimeStamp: asset.attributes.firstAddedToSearchIndexDatetime
                    ? new Date(asset.attributes.firstAddedToSearchIndexDatetime)
                        .toLocaleDateString(dateLocaleCode, dateOptions) : null,
                updatedTimeStamp: asset.attributes.fileChangeTimestamp
                    ? new Date(asset.attributes.fileChangeTimestamp).toLocaleDateString(dateLocaleCode, dateOptions)
                    : null
            };

            assetData.push(data);
        });

        return assetData;
    }

    /**
     * @param {HTMLElement} element Asset data as json
     */
    initEvents(element)
    {
        element.querySelectorAll('.js-media-library__asset--single-download').forEach(asset =>
        {
            let filePath = asset.dataset.assetFilePath;

            asset.addEventListener('click', e =>
            {
                e.preventDefault();

                this.downloadAsset(filePath);
            });
        });
    }

    /**
     * @param {HTMLElement} container Container element
     * @param {HTMLElement} element Asset element
     * @param {Object} asset Asset data
     */
    renderAsset(container, element, asset)
    {
        element.querySelector('.js-media-library__asset-template--image').src = asset.thumbnail;

        container.append(element);
    }

    /**
	 * @param {string} filePath file path
	 */
    downloadAsset(filePath)
    {
        this.mediaLibraryClient.downloadAsset(filePath)
            .then(response => response.json())
            .then(json =>
            {
                window.location.href = this.downloadPath + json;
            })
            .catch(() =>
            {
                displayErrorFeedbackMessage(this.downloadFailedFeedback);
            });
    }
}
